import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import back_n from "./Image/back_n.gif";
import back_o from "./Image/back_o.gif";
import back_d from "./Image/back_d.gif";
import principle_n from "./Image/principle_n.gif";
import tutorials_n from "./Image/tutorials_n.gif";
import clue_n from "./Image/clue_n.gif";
import solution_n from "./Image/solution_n.gif";
import solution_o from "./Image/solution_o.gif";
import solution_d from "./Image/solution_d.gif";
import next_n from "./Image/next_n.gif";
import next_o from "./Image/next_o.gif";
import next_d from "./Image/next_d.gif";
import ok_n from "./Image/ok_n.gif";
import ok_o from "./Image/ok_o.gif";
import ok_d from "./Image/ok_d.gif";
import done_o from "./Image/done_o.gif";
import done_n from "./Image/done_n.gif";
import x_close_btn_n from "./Image/x_close_btn_n.png";
import x_close_btn_o from "./Image/x_close_btn_o.png";
import next_step_btn_n from "./Image/next_step_btn_n.png";
import next_step_btn_o from "./Image/next_step_btn_o.png";
import got_it_btn_n from "./Image/got_it_btn_n.png";
import got_it_btn_o from "./Image/got_it_btn_o.png";
import './Style/Question.css';
import Loading from "../../Common/Loading";
import { saveQuestionPerformance, QuestionPerformancePayload } from '../../services/questionApi';

export interface AnswerRef {
    getAnswer: () => {
        marks: number;
        noOfOptions: number;
        enteredCorrectAnswer: string;
        enteredWrongAnswer: string;
        enteredBlankAnswer: string;
        scoringMethod?: number | null;
        optOrder?: string;
    } | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Subtype discriminator
//   'fill'    – F-engine inside L: word-bank on right, blank inputs on left
//   'order'   – classic ordering list (initial-order + answer-key hidden inputs
//               OR TD2_* image cells)
// ─────────────────────────────────────────────────────────────────────────────
type LSubtype = 'fill' | 'order';

const lQnsStyles = `
    .l-qns-container {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding-top: 20px;
        height: 100%;
        width: 100%;
        overflow: auto;
        background-color: #f8fafc;
    }
    .l-qns-main {
        flex: 1;
        display: flex;
        justify-content: center;
    }
    .l-qns-main table[name="TableQn"] {
        margin: 0 auto;
    }
    .l-qns-main table, .l-qns-main tr, .l-qns-main td {
        background-color: transparent !important;
        text-align: center;
    }
    .l-qns-main table[name="TableQn"] * {
        margin-left: auto !important;
        margin-right: auto !important;
        text-align: center !important;
    }
    .l-qns-divider {
        width: 10px;
        height: 80vh;
        background-repeat: no-repeat;
        background-size: contain;
    }
    .l-qns-options-panel {
        width: 310px;
        padding: 0 10px;
        font-family: arial;
    }
    .l-qns-options-header {
        font-size: 20pt;
        color: #3c5168;
        padding-bottom: 10px;
    }
    .l-qns-option-item {
        min-height: 70px;
        margin-top: 10px;
        padding: 8px 12px;
        font-size: 20px;
        font-family: 'Arial';
        color: black;
        cursor: pointer;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 12px;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        border: none;
    }
    .l-fill-word-card.used {
        opacity: 0.35;
        cursor: default;
        pointer-events: none;
    }

    .feedback-box {
        width: 40px;
        height: 40px;
        border: none;
        background: transparent;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 10px;
    }
    .item-text {
        flex-grow: 1;
        text-align: center;
        padding-right: 55px;
    }
`;

interface LQnsProps {
    lessonMeta?: any;
    questionHtml: string;
    currentPage: number;
    totalPages: number;
    goToNextPage?: (e: React.MouseEvent) => void;
    showAlert: (message: string) => void;
    correctAudio: string[];
    incorrectAudio: string[];
    assetsBaseUrl?: string;
    sessionId: number | null;
    isCompleting?: boolean;
    updateSessionPoints?: (pointsEarned: number) => void;
    hwid?: number | null;
    subject?: string;
}

const LQns: React.ForwardRefRenderFunction<AnswerRef, LQnsProps> = (props, ref) => {
    const {
        lessonMeta, questionHtml, currentPage, totalPages,
        goToNextPage, correctAudio, incorrectAudio, assetsBaseUrl,
        showAlert, sessionId, isCompleting, updateSessionPoints, hwid, subject
    } = props;

    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const folderParam = params.get('folder');
    const lessonName = params.get('name');
    const diccode = params.get('diccode');
    const calledFrom = params.get('calledFrom');

    const solnIframeRef = useRef<HTMLIFrameElement>(null);
    const clueIframeRef = useRef<HTMLIFrameElement>(null);
    const prcIframeRef = useRef<HTMLIFrameElement>(null);

    // ── button image state ────────────────────────────────────────────────────
    const [backSrc, setBackSrc] = useState(back_n);
    const [nextSrc, setNextSrc] = useState(next_n);
    const [okSrc, setOkSrc] = useState(ok_n);
    const [doneSrc, setDoneSrc] = useState(done_n);
    const [okClicked, setOkClicked] = useState(false);
    const [currentQnMeta, setCurrentQnMeta] = useState<any | null>(null);
    const [CloseBtnSrc, setCloseBtnSrc] = useState(x_close_btn_n);
    const [NextStepBtnSrc, setNextStepBtnSrc] = useState(next_step_btn_n);
    const [GotItBtnSrc, setGotItBtnSrc] = useState(got_it_btn_n);
    const [solutionSrc, setSolutionSrc] = useState(solution_n);

    // ── subtype + shared state ────────────────────────────────────────────────
    const [lSubtype, setLSubtype] = useState<LSubtype>('order');
    const [questionContent, setQuestionContent] = useState<Document | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [startTime, setStartTime] = useState<Date | null>(null);
    const [solutionSeen, setSolutionSeen] = useState<string | number>('N');
    const [clueSeen, setClueSeen] = useState(false);
    const [prcSeen, setPrcSeen] = useState(false);
    const isInAssessment = window.location.href.includes('Assessment');

    // ── ORDER subtype state ───────────────────────────────────────────────────
    const [items, setItems] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [feedback, setFeedback] = useState<(boolean | null)[]>([]);
    const [bgUrls, setBgUrls] = useState({ normal: '', selected: '' });
    const [dividerUrl, setDividerUrl] = useState('');
    const correctAnswerOrder = useRef<string[]>([]);

    // ── FILL subtype state ────────────────────────────────────────────────────
    // wordBank  : the options shown on the right (KSD_WLIST_DragDrop_*)
    // blanks    : what the student has filled into each slot (parallel to KSD_BLANK_*)
    // correctAnswers: the correct value for each blank position (from KSD_BW split by |)
    // selectedWord : index into wordBank that the student has "picked" (null = none)
    const [wordBank, setWordBank] = useState<string[]>([]);
    const [blanks, setBlanks] = useState<string[]>([]);          // '' means unfilled
    const blanksRef = useRef<string[]>([]);                       // always-current for getAnswer
    const [fillCorrectAnswers, setFillCorrectAnswers] = useState<string[]>([]);
    const fillCorrectAnswersRef = useRef<string[]>([]);           // always-current for getAnswer
    const lSubtypeRef = useRef<LSubtype>('order');                // always-current for getAnswer
    const [fillFeedback, setFillFeedback] = useState<(boolean | null)[]>([]);

    // ── modal state ───────────────────────────────────────────────────────────
    const [showClueModal, setShowClueModal] = useState(false);
    const [clueContent, setClueContent] = useState('');
    const [isClueLoading, setIsClueLoading] = useState(false);
    const [noOfClues, setNoOfClues] = useState(0);
    const [currentClueNo, setCurrentClueNo] = useState(0);
    const [showPrcModal, setShowPrcModal] = useState(false);
    const [prcContent, setPrcContent] = useState("");
    const [isPrcLoading, setIsPrcLoading] = useState(false);
    const [principleList, setPrincipleList] = useState<string[]>([]);
    const [currentPrcIndex, setCurrentPrcIndex] = useState(0);
    const [showSolnModal, setShowSolnModal] = useState(false);
    const [isSolnLoading, setIsSolnLoading] = useState(false);
    const [solnContent, setSolnContent] = useState("");
    const [currentSolnNo, setCurrentSolnNo] = useState(0);
    const [noOfSteps, setNoOfSteps] = useState(0);

    // ─────────────────────────────────────────────────────────────────────────
    // Parse question HTML on page change
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        // Reset ALL state
        setOkClicked(false);
        setItems([]);
        setSelectedIndex(null);
        setFeedback([]);
        setWordBank([]);
        setBlanks([]);
        blanksRef.current = [];
        setFillCorrectAnswers([]);
        fillCorrectAnswersRef.current = [];
        setFillFeedback([]);
        setStartTime(new Date());
        setSolutionSeen('N');
        setClueSeen(false);
        setPrcSeen(false);
        correctAnswerOrder.current = [];

        if (lessonMeta?.qns) {
            setCurrentQnMeta(lessonMeta.qns[currentPage - 1]);
        }

        if (!questionHtml) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(questionHtml, 'text/html');
        setQuestionContent(doc);

        // ── Detect subtype ────────────────────────────────────────────────────
        const initialOrderEl  = doc.getElementById('initial-order') as HTMLInputElement | null;
        const answerKeyEl     = doc.getElementById('answer-key')    as HTMLInputElement | null;
        const ksdBwEl         = doc.getElementById('KSD_BW')        as HTMLInputElement | null;
        const dragDropEls     = doc.querySelectorAll('[id^="KSD_WLIST_DragDrop_"]');
        const blankEls        = doc.querySelectorAll('[id^="KSD_BLANK_"]');
        const td2Els          = doc.querySelectorAll('[id^="TD2_"]');

        // ── FILL subtype: KSD_WLIST_DragDrop_* + KSD_BLANK_* present
        //    (covers the "F-engine inside L" pattern you showed in the screenshots)
        if (dragDropEls.length > 0 && blankEls.length > 0) {
            setLSubtype('fill');
            lSubtypeRef.current = 'fill';

            // Word bank options
            const words = Array.from(dragDropEls).map(el => el.textContent?.trim() || '');
            setWordBank(words);

            // Blank slots – start all empty
            const emptyBlanks = Array(blankEls.length).fill('');
            setBlanks(emptyBlanks);
            blanksRef.current = emptyBlanks;
            setFillFeedback(Array(blankEls.length).fill(null));

            // Correct answers: read KSD_WLIST_PopUp_* onclick attrs to find which
            // blank each word belongs in.
            // e.g. onclick="dropFromWLIST(this, 2, 0)" → this word goes into blank 2 (1-based)
            const numBlanks = blankEls.length;
            const correctByBlank: string[] = Array(numBlanks).fill('');
            let mappingFound = false;

            Array.from(doc.querySelectorAll('[id^="KSD_WLIST_PopUp_"]') as NodeListOf<Element>).forEach(el => {
                const onclick = el.getAttribute('onclick') || '';
                const match = onclick.match(/dropFromWLIST\s*\(\s*this\s*,\s*(\d+)/);
                if (match) {
                    const blankIdx = parseInt(match[1], 10) - 1; // 0-based
                    const word = (el as HTMLElement).textContent?.trim() || '';
                    if (blankIdx >= 0 && blankIdx < numBlanks && word) {
                        correctByBlank[blankIdx] = word;
                        mappingFound = true;
                    }
                }
            });

            if (mappingFound) {
                setFillCorrectAnswers(correctByBlank);
                fillCorrectAnswersRef.current = correctByBlank;
            } else if (ksdBwEl) {
                // Fallback: KSD_BW order matches blank order
                const ksdBwAnswers = ksdBwEl.value.split('|').map((s: string) => s.trim());
                setFillCorrectAnswers(ksdBwAnswers);
                fillCorrectAnswersRef.current = ksdBwAnswers;
            } else {
                setFillCorrectAnswers(words);
                fillCorrectAnswersRef.current = words;
            }

            // bg / divider (may or may not exist)
            const normalBgEl   = doc.getElementById('normal-bg-url')   as HTMLInputElement | null;
            const selectedBgEl = doc.getElementById('selected-bg-url') as HTMLInputElement | null;
            const dividerUrlEl = doc.getElementById('divider-bar-url') as HTMLInputElement | null;
            if (normalBgEl && selectedBgEl) setBgUrls({ normal: normalBgEl.value, selected: selectedBgEl.value });
            if (dividerUrlEl) setDividerUrl(dividerUrlEl.value);

            return; // done
        }

        // ── ORDER subtype ─────────────────────────────────────────────────────
        setLSubtype('order');
        lSubtypeRef.current = 'order';

        const normalBgEl   = doc.getElementById('normal-bg-url')   as HTMLInputElement | null;
        const selectedBgEl = doc.getElementById('selected-bg-url') as HTMLInputElement | null;
        const dividerUrlEl = doc.getElementById('divider-bar-url') as HTMLInputElement | null;
        if (normalBgEl && selectedBgEl) setBgUrls({ normal: normalBgEl.value, selected: selectedBgEl.value });
        if (dividerUrlEl) setDividerUrl(dividerUrlEl.value);

        if (initialOrderEl && answerKeyEl) {
            const parsedItems   = JSON.parse(initialOrderEl.value || '[]') as string[];
            const parsedAnswers = JSON.parse(answerKeyEl.value   || '[]') as string[];
            const hasRealValues = parsedItems.some(x => x && x.trim() !== '');

            if (hasRealValues) {
                setItems(parsedItems);
                correctAnswerOrder.current = parsedAnswers;
                return;
            }

            // Fallback to image cells
            const imageOptions = Array.from(td2Els).map(td => {
                const img = td.querySelector('img');
                return img ? img.outerHTML : (td as HTMLElement).innerHTML || '';
            });
            setItems(imageOptions);
            correctAnswerOrder.current = [...imageOptions];
            return;
        }

        // Last resort: plain text from KSD_WLIST_DragDrop_* (no blanks present)
        const textOptions = Array.from(
            doc.querySelectorAll('[id^="KSD_WLIST_DragDrop_"]')
        ).map(el => el.textContent?.trim() || '');

        if (textOptions.length > 0) {
            setItems(textOptions);
            correctAnswerOrder.current = [...textOptions];
        }
    }, [currentPage, questionHtml, lessonMeta]);

    // ─────────────────────────────────────────────────────────────────────────
    // ORDER subtype interaction
    // ─────────────────────────────────────────────────────────────────────────
    const handleItemClick = (clickedIndex: number) => {
        if (okClicked) return;

        if (selectedIndex === clickedIndex) {
            setSelectedIndex(null);
        } else if (selectedIndex === null) {
            setSelectedIndex(clickedIndex);
        } else {
            const newItems = [...items];
            const itemToMove = newItems.splice(selectedIndex, 1)[0];
            newItems.splice(clickedIndex, 0, itemToMove);
            setItems(newItems);
            setSelectedIndex(null);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // FILL subtype: click a word card → auto-fills next empty blank.
    // Click a used word card → clears it from its blank (toggle off).
    // ─────────────────────────────────────────────────────────────────────────
    const handleWordCardClick = (wordIndex: number) => {
        if (okClicked) return;
        const word = wordBank[wordIndex];

        // If this word is already placed somewhere, remove it (toggle off)
        const existingSlot = blanks.findIndex(b => b === word);
        if (existingSlot !== -1) {
            const newBlanks = [...blanks];
            newBlanks[existingSlot] = '';
            setBlanks(newBlanks);
            blanksRef.current = newBlanks;
            return;
        }

        // Find the next empty blank and fill it
        const nextEmpty = blanks.findIndex(b => b === '');
        if (nextEmpty === -1) return; // all filled already

        const newBlanks = [...blanks];
        newBlanks[nextEmpty] = word;
        setBlanks(newBlanks);
        blanksRef.current = newBlanks;
    };

    // Derive which words are already placed (used) in some blank
    const usedWords = new Set(blanks.filter(b => b !== ''));

    // ─────────────────────────────────────────────────────────────────────────
    // Scoring helpers (used by both getAnswer and handleOkClick)
    // ─────────────────────────────────────────────────────────────────────────
    const computeOrderScore = useCallback(() => {
        const allCorrect = items.every((item, i) => item === correctAnswerOrder.current[i]);
        const userStr = items.join('|');
        return {
            marks: allCorrect ? 1 : 0,
            noOfOptions: items.length,
            enteredCorrectAnswer: allCorrect ? userStr : '',
            enteredWrongAnswer: !allCorrect ? userStr : '',
            enteredBlankAnswer: '',
            allCorrect,
            newFeedback: items.map((item, i) => item === correctAnswerOrder.current[i]),
        };
    }, [items]);

    const computeFillScore = useCallback(() => {
        const hasBlank = blanks.some(b => b === '');
        if (hasBlank) {
            return null; // signal "incomplete"
        }
        const newFeedback = blanks.map((b, i) => b.trim() === (fillCorrectAnswers[i] || '').trim());
        const allCorrect = newFeedback.every(Boolean);
        const userStr = blanks.join('|');
        return {
            marks: allCorrect ? 1 : 0,
            noOfOptions: blanks.length,
            enteredCorrectAnswer: allCorrect ? userStr : '',
            enteredWrongAnswer: !allCorrect ? userStr : '',
            enteredBlankAnswer: hasBlank ? userStr : '',
            allCorrect,
            newFeedback,
        };
    }, [blanks, fillCorrectAnswers]);

    // ─────────────────────────────────────────────────────────────────────────
    // Expose getAnswer via ref
    // ─────────────────────────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
        getAnswer: () => {
            // Always read from refs — never from stale useCallback closures
            if (lSubtypeRef.current === 'fill') {
                const currentBlanks = blanksRef.current;
                const currentCorrect = fillCorrectAnswersRef.current;
                const hasBlank = currentBlanks.some(b => b === '');
                const feedback = currentBlanks.map((b, i) => b.trim() === (currentCorrect[i] || '').trim());
                const allCorrect = !hasBlank && feedback.every(Boolean);
                const userStr = currentBlanks.join('|');
                console.log('[LQns getAnswer] blanks:', currentBlanks, 'correct:', currentCorrect, 'feedback:', feedback);
                return {
                    marks: allCorrect ? 1 : 0,
                    noOfOptions: currentBlanks.length,
                    enteredCorrectAnswer: allCorrect ? userStr : '',
                    enteredWrongAnswer: !allCorrect ? userStr : '',
                    enteredBlankAnswer: hasBlank ? userStr : '',
                    optOrder: '',
                    scoringMethod: null,
                };
            }
            // order — correctAnswerOrder.current is already a ref, safe
            const currentItems = items;
            const allCorrect = currentItems.every((item, i) => item === correctAnswerOrder.current[i]);
            const userStr = currentItems.join('|');
            return {
                marks: allCorrect ? 1 : 0,
                noOfOptions: currentItems.length,
                enteredCorrectAnswer: allCorrect ? userStr : '',
                enteredWrongAnswer: !allCorrect ? userStr : '',
                enteredBlankAnswer: '',
                optOrder: '',
                scoringMethod: null,
            };
        }
    }));

    // ─────────────────────────────────────────────────────────────────────────
    // Audio
    // ─────────────────────────────────────────────────────────────────────────
    const playRandomSound = useCallback(async (soundFiles: string[], folder: 'Correct' | 'Incorrect') => {
        if (!soundFiles || soundFiles.length === 0) return;
        const fileName = soundFiles[Math.floor(Math.random() * soundFiles.length)];
        const audioUrl = `${assetsBaseUrl}Audio/Question/${folder}/${fileName}`;
        const audio = new Audio(audioUrl);
        try { await audio.play(); } catch (e) { console.error(e); }
    }, [assetsBaseUrl]);

    // ─────────────────────────────────────────────────────────────────────────
    // DB save
    // ─────────────────────────────────────────────────────────────────────────
    const handleDbUpdate = useCallback((payload: QuestionPerformancePayload) => {
        return saveQuestionPerformance(payload)
            .then(data => {
                if (isCompleting && updateSessionPoints) updateSessionPoints(data.pointsEarned || 0);
                return data;
            })
            .catch(error => { console.error('Save Performance Error:', error); throw error; });
    }, [isCompleting, updateSessionPoints]);

    // ─────────────────────────────────────────────────────────────────────────
    // OK click – handles both subtypes
    // ─────────────────────────────────────────────────────────────────────────
    const handleOkClick = useCallback(async (e: React.MouseEvent<any>) => {
        e.preventDefault();
        if (okClicked || isSubmitting) return;

        // ── Score ─────────────────────────────────────────────────────────────
        let marks = 0;
        let noOfOptions = 0;
        let enteredCorrectAnswer = '';
        let enteredWrongAnswer = '';
        let enteredBlankAnswer = '';
        let allCorrect = false;
        let newFeedback: boolean[] = [];

        if (lSubtype === 'fill') {
            const hasEmpty = blanks.some(b => b === '');
            if (hasEmpty) {
                showAlert('Please fill in all the blanks before checking.');
                return;
            }
            // Score inline — do NOT use computeFillScore() which may read stale closure
            newFeedback = blanks.map((b, i) => b.trim() === (fillCorrectAnswers[i] || '').trim());
            allCorrect = newFeedback.every(Boolean);
            const userStr = blanks.join('|');
            marks = allCorrect ? 1 : 0;
            noOfOptions = blanks.length;
            enteredCorrectAnswer = allCorrect ? userStr : '';
            enteredWrongAnswer = !allCorrect ? userStr : '';
        } else {
            const result = computeOrderScore();
            marks = result.marks;
            noOfOptions = result.noOfOptions;
            enteredCorrectAnswer = result.enteredCorrectAnswer;
            enteredWrongAnswer = result.enteredWrongAnswer;
            allCorrect = result.allCorrect;
            newFeedback = result.newFeedback;
        }

        // ── Build payload ─────────────────────────────────────────────────────
        const questionAnswerTime = startTime
            ? Math.floor((new Date().getTime() - startTime.getTime()) / 1000)
            : 0;

        const payload: QuestionPerformancePayload = {
            schoolCode:    sessionStorage.getItem('SchoolCode')    || '',
            familyCode:    sessionStorage.getItem('FamilyCode')    || '',
            login:         sessionStorage.getItem('Login')         || '',
            dicCode:       sessionStorage.getItem('SelectedDICFile') || '',
            lsnTestCode:   sessionStorage.getItem('LsnTestCode')   || '',
            sessionId:     sessionId || 1,
            qnNo:          currentQnMeta?.QnNo,
            subNo:         0,
            qnType:        currentQnMeta?.QnType,
            marks,
            noOfOptions,
            enteredCorrectAnswer,
            enteredWrongAnswer,
            enteredBlankAnswer,
            solutionSeen:  (typeof solutionSeen === 'number' && solutionSeen > 0) || solutionSeen === 'Y' ? 'Y' : 'N',
            clueSeen,
            principleSeen: prcSeen,
            questionLoadTime:   0,
            questionAnswerTime,
            lastQnInd:     currentQnMeta?.LastQnInd || currentPage,
            allQuestionsCompleted: isCompleting ? 'True' : 'False',
            chapterCode:   sessionStorage.getItem('ChapterCode') || '',
            unitCode:      sessionStorage.getItem('UnitCode')    || '',
            hwid,
            subject,
        };

        // ── Submit ────────────────────────────────────────────────────────────
        const isLastQuestion = currentPage === totalPages;
        if (isLastQuestion) {
            setIsSubmitting(true);
            try {
                await handleDbUpdate(payload);
            } catch {
                showAlert('Error saving progress. Please try again.');
                setIsSubmitting(false);
                return;
            }
            setIsSubmitting(false);
        } else {
            handleDbUpdate(payload).catch(e => console.error('Background save failed', e));
        }

        // ── UI feedback (set AFTER submit, not before) ────────────────────────
        if (lSubtype === 'fill') {
            setFillFeedback(newFeedback);
        } else {
            setFeedback(newFeedback);
        }

        if (allCorrect) {
            playRandomSound(correctAudio, 'Correct');
        } else {
            playRandomSound(incorrectAudio, 'Incorrect');
            if (currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0) {
                handleSolnClick(true);
            }
        }

        setOkClicked(true);
    }, [
        lSubtype, okClicked, isSubmitting, blanks, items,
        computeFillScore, computeOrderScore,
        correctAudio, incorrectAudio, playRandomSound,
        startTime, currentQnMeta, currentPage, totalPages,
        sessionId, isCompleting, handleDbUpdate,
        hwid, subject, clueSeen, prcSeen, solutionSeen, showAlert
    ]);

    // ─────────────────────────────────────────────────────────────────────────
    // Clue
    // ─────────────────────────────────────────────────────────────────────────
    const handleClueClick = useCallback(async () => {
        if (!currentQnMeta || !lessonMeta) return;
        setShowClueModal(true);
        setIsClueLoading(true);
        setClueContent('');
        setCurrentClueNo(0);
        setNoOfClues(0);

        const fp = new URLSearchParams(window.location.search).get('folder');
        const country = params.get('country') || 'Australia';
        const varValues = currentQnMeta?.VarValues || '';
        if (!fp) { showAlert('Could not determine the lesson folder.'); setIsClueLoading(false); setShowClueModal(false); return; }

        const accessToken = sessionStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

        try {
            const response = await fetch(`${apiUrl}api/clue/`, {
                method: 'POST', headers,
                body: JSON.stringify({ folder: fp, questionNo: currentQnMeta.QnNo, country, varValues }),
            });
            if (!response.ok) {
                if (response.status === 404) { setClueContent(''); }
                else { if (response.status === 401 || response.status === 403) showAlert('Authentication failed.'); throw new Error(`HTTP ${response.status}`); }
            } else {
                const data = await response.json();
                setClueContent(data.clueHtml || '');
                setClueSeen(true);
            }
        } catch (error) {
            console.error('Error fetching clue:', error);
            setClueContent('');
        } finally {
            setIsClueLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, params]);

    // ─────────────────────────────────────────────────────────────────────────
    // Solution
    // ─────────────────────────────────────────────────────────────────────────
    const handleSolnClick = useCallback(async (autoExpand = false) => {
        if (!currentQnMeta || !lessonMeta) return;
        setShowSolnModal(true);
        setIsSolnLoading(true);
        setSolnContent('');
        setCurrentSolnNo(0);
        setNoOfSteps(0);

        const fp = new URLSearchParams(window.location.search).get('folder');
        const country = new URLSearchParams(window.location.search).get('country') || 'Australia';
        if (!fp) { showAlert('Could not determine the lesson folder.'); setIsSolnLoading(false); setShowSolnModal(false); return; }

        const accessToken = sessionStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

        try {
            const response = await fetch(`${apiUrl}api/solution/`, {
                method: 'POST', headers,
                body: JSON.stringify({ folder: fp, questionNo: currentQnMeta.QnNo, country, autoExpand }),
            });
            if (!response.ok) { if (response.status === 401 || response.status === 403) showAlert('Authentication failed.'); throw new Error(`HTTP ${response.status}`); }
            const data = await response.json();
            setSolnContent(data.solutionHtml || '');
            if (solutionSeen === 'N') setSolutionSeen(1);
        } catch (error) {
            console.error('Error fetching solution:', error);
            setShowSolnModal(false);
        } finally {
            setIsSolnLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, solutionSeen]);

    // ─────────────────────────────────────────────────────────────────────────
    // Principle
    // ─────────────────────────────────────────────────────────────────────────
    const fetchPrincipleContent = async (principleId: string, fp: string, country: string) => {
        const accessToken = sessionStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;
        const response = await fetch(`${apiUrl}api/principle/`, {
            method: 'POST', headers,
            body: JSON.stringify({ folder: fp, questionNo: principleId, country }),
        });
        if (!response.ok) throw new Error('Failed');
        return response.json();
    };

    const handlePrcClick = useCallback(async () => {
        if (!currentQnMeta || !lessonMeta) return;
        const prcString = currentQnMeta.PrincipleNos || '';
        const prcIds = prcString.replace(/"/g, '').split(',').filter((s: string) => s.trim() !== '');
        if (prcIds.length === 0) {
            if ((currentQnMeta.NoOfPrc || 0) > 0) prcIds.push('1');
            else { showAlert('No principles found for this question.'); return; }
        }
        setShowPrcModal(true);
        setIsPrcLoading(true);
        setPrcContent('');
        setPrincipleList(prcIds);
        setCurrentPrcIndex(0);
        const fp = new URLSearchParams(window.location.search).get('folder');
        const country = params.get('country') || 'Australia';
        if (!fp) { showAlert('Could not determine the lesson folder.'); setIsPrcLoading(false); setShowPrcModal(false); return; }
        try {
            const data = await fetchPrincipleContent(prcIds[0], fp, country);
            setPrcContent(data.principleHtml || '');
            setPrcSeen(true);
        } catch { setPrcContent(''); }
        finally { setIsPrcLoading(false); }
    }, [currentQnMeta, lessonMeta, showAlert, params]);

    // ─────────────────────────────────────────────────────────────────────────
    // Back
    // ─────────────────────────────────────────────────────────────────────────
    const handleBack = () => {
        const cf = params.get('calledFrom');
        if (cf) window.location.href = decodeURIComponent(cf);
        else if (window.history.length > 1) window.history.back();
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Ref to the left panel div — used by fill subtype to poke values into the
    // KSD_BLANK_* inputs that are rendered as part of the raw question HTML.
    // We NEVER replace the original HTML; we only set .value on those inputs.
    // ─────────────────────────────────────────────────────────────────────────
    const mainContentRef = useRef<HTMLDivElement>(null);

    // After every blanks[] change, sync values into the real DOM inputs
    useEffect(() => {
        if (lSubtype !== 'fill' || !mainContentRef.current) return;
        blanks.forEach((val, i) => {
            const input = mainContentRef.current!.querySelector<HTMLInputElement>(`#KSD_BLANK_${i}`);
            if (input) input.value = val;
        });
    }, [blanks, lSubtype]);

    // After okClicked + fillFeedback set, colour the inputs
    useEffect(() => {
        if (lSubtype !== 'fill' || !mainContentRef.current || fillFeedback.every(f => f === null)) return;
        fillFeedback.forEach((fb, i) => {
            const input = mainContentRef.current!.querySelector<HTMLInputElement>(`#KSD_BLANK_${i}`);
            if (!input) return;
            if (fb === true)  { input.style.color = 'green'; input.style.borderBottomColor = 'green'; }
            if (fb === false) { input.style.color = 'red';   input.style.borderBottomColor = 'red';   }
        });
    }, [fillFeedback, lSubtype]);

    // ─────────────────────────────────────────────────────────────────────────
    // Render: main question content (left panel) — same for BOTH subtypes
    // ─────────────────────────────────────────────────────────────────────────
    const renderMainContent = () => {
        if (!questionContent) return <Loading />;
        const qnTable = questionContent.querySelector('table[name="TableQn"]');
        return qnTable
            ? <div ref={mainContentRef} className="l-qns-main" dangerouslySetInnerHTML={{ __html: qnTable.outerHTML }} />
            : null;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render: right-side options panel (differs by subtype)
    // ─────────────────────────────────────────────────────────────────────────
    const CORRECT_URL = 'https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF';
    const WRONG_URL   = 'https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsWrong.GIF';

    const renderOptionsPanel = () => {
        if (lSubtype === 'fill') {
            return (
                <div className="l-qns-options-panel">
                    <div className="l-qns-options-header">Your Options:</div>
                    <div>
                        {wordBank.map((word, wi) => {
                            const isUsed = usedWords.has(word);
                            return (
                                <div
                                    key={wi}
                                    className={`l-qns-option-item l-fill-word-card${isUsed ? ' used' : ''}`}
                                    style={{ backgroundImage: `url(${bgUrls.normal})` }}
                                    onClick={() => handleWordCardClick(wi)}
                                >
                                    <div className="feedback-box" />
                                    <div className="item-text">{word}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        // ORDER subtype
        return (
            <div className="l-qns-options-panel">
                <div className="l-qns-options-header">Your Options:</div>
                <div>
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className="l-qns-option-item"
                            style={{ backgroundImage: `url(${selectedIndex === index ? bgUrls.selected : bgUrls.normal})` }}
                            onClick={() => handleItemClick(index)}
                        >
                            <div className="feedback-box">
                                {feedback.length > 0 && (
                                    <img
                                        src={feedback[index] ? CORRECT_URL : WRONG_URL}
                                        alt={feedback[index] ? 'Correct' : 'Incorrect'}
                                        style={{ height: '20px' }}
                                    />
                                )}
                            </div>
                            <div className="item-text" dangerouslySetInnerHTML={{ __html: item }} />
                        </div>
                    ))}
                </div>
            </div>
        );
    };



    // ─────────────────────────────────────────────────────────────────────────
    // JSX
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <style>{lQnsStyles}</style>

            {!isInAssessment && (
                <tr id="trQnButtons">
                    <td className="QnPanelT">{/* nav buttons commented out as in original */}</td>
                </tr>
            )}

            {questionHtml ? (
                <tr id="triFrame">
                    <td id="tdFrameQnT" style={{ height: '100%', padding: 0 }}>
                        <div style={{ height: 'calc(95vh - 50px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="l-qns-container">
                                {/* Left / centre: question HTML — same for both subtypes */}
                                {renderMainContent()}

                                <div className="l-qns-divider" style={{ backgroundImage: `url(${dividerUrl})` }} />

                                {/* Right: word bank or ordering list */}
                                {renderOptionsPanel()}
                            </div>

                            {/* ── Bottom action bar ── */}
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 0', gap: 12 }}>
                                {isSubmitting ? (
                                    <Loading />
                                ) : okClicked ? (
                                    currentPage === totalPages ? (
                                        <img
                                            src={doneSrc}
                                            alt="Done"
                                            style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                                            onClick={() => {
                                                const dicCode    = sessionStorage.getItem('SelectedDICFile') || '';
                                                const lsnTestCode = sessionStorage.getItem('LsnTestCode') || '';
                                                const cf = params.get('calledFrom') || '';
                                                navigate(
                                                    `/STUDENT/Tutorials/Result?DicCode=${dicCode}&LsnTestCode=${lsnTestCode}&SessionID=${sessionId}&source=lesson` +
                                                    (cf ? `&calledFrom=${encodeURIComponent(cf)}` : '')
                                                );
                                            }}
                                            onMouseOver={() => setDoneSrc(done_o)}
                                            onMouseOut={() => setDoneSrc(done_n)}
                                        />
                                    ) : (
                                        <img
                                            src={nextSrc}
                                            alt="Next"
                                            style={{ cursor: 'pointer' }}
                                            onClick={goToNextPage}
                                            onMouseOver={() => setNextSrc(next_o)}
                                            onMouseOut={() => setNextSrc(next_n)}
                                            onMouseDown={() => setNextSrc(next_d)}
                                            onMouseUp={() => setNextSrc(next_o)}
                                        />
                                    )
                                ) : (
                                    <div
                                        style={{ display: 'inline-block', cursor: 'pointer' }}
                                        onClick={handleOkClick}
                                        onMouseOver={() => setOkSrc(ok_o)}
                                        onMouseOut={() => setOkSrc(ok_n)}
                                        onMouseDown={() => setOkSrc(ok_d)}
                                        onMouseUp={() => setOkSrc(ok_o)}
                                    >
                                        {/* <img src={okSrc} alt="OK" style={{ marginTop: 3 }} /> */}
                                    </div>
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            ) : (
                <tr><td><Loading /></td></tr>
            )}

            {/* ── Clue modal ── */}
            {showClueModal && (
                <div className="modal-overlay" onClick={() => setShowClueModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span>Clue</span>
                            <img src={CloseBtnSrc} alt="Close" onClick={() => setShowClueModal(false)}
                                onMouseOver={() => setCloseBtnSrc(x_close_btn_o)} onMouseOut={() => setCloseBtnSrc(x_close_btn_n)}
                                style={{ cursor: 'pointer' }} />
                        </div>
                        <div className="modal-body">
                            {isClueLoading ? <Loading /> : (
                                <iframe ref={clueIframeRef} title="Clue" srcDoc={clueContent}
                                    style={{ width: '100%', border: 'none', minHeight: '80px' }}
                                    onLoad={() => {
                                        const iframe = clueIframeRef.current;
                                        if (!iframe?.contentDocument?.body) return;
                                        try {
                                            iframe.contentDocument.querySelectorAll("[id^='divShow']").forEach((div, i) => {
                                                (div as HTMLElement).style.display = i === 0 ? 'block' : 'none';
                                                (div as HTMLElement).style.padding = '20px';
                                            });
                                            const cluesInput = iframe.contentDocument.getElementById('NoOfClues') as HTMLInputElement;
                                            setNoOfClues(parseInt(cluesInput?.value || '0', 10));
                                            const first = iframe.contentDocument.getElementById('divShow1');
                                            if (first) iframe.style.height = `${Math.min(first.scrollHeight, 300)}px`;
                                        } catch (e) { console.error(e); }
                                    }}
                                />
                            )}
                        </div>
                        <div className="modal-footer">
                            {!isClueLoading && noOfClues > 1 && currentClueNo < noOfClues - 1 && (
                                <img src={NextStepBtnSrc} alt="Next Clue" style={{ cursor: 'pointer' }}
                                    onMouseOver={() => setNextStepBtnSrc(next_step_btn_o)} onMouseOut={() => setNextStepBtnSrc(next_step_btn_n)}
                                    onClick={() => {
                                        const next = currentClueNo + 1;
                                        const iframe = clueIframeRef.current;
                                        if (iframe?.contentDocument) {
                                            iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]').forEach((div, i) => {
                                                div.style.display = i === next ? 'block' : 'none';
                                            });
                                            setCurrentClueNo(next);
                                        }
                                    }}
                                />
                            )}
                            {!isClueLoading && (noOfClues <= 1 || currentClueNo === noOfClues - 1) && (
                                <img src={GotItBtnSrc} alt="Got It" style={{ cursor: 'pointer' }}
                                    onMouseOver={() => setGotItBtnSrc(got_it_btn_o)} onMouseOut={() => setGotItBtnSrc(got_it_btn_n)}
                                    onClick={() => setShowClueModal(false)} />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Principle modal ── */}
            {showPrcModal && (
                <>
                    <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => setShowPrcModal(false)} />
                    <div id="divPCS" style={{ display: 'block' }} className="principle">
                        <table id="tblTitlePCS" className="principle" cellSpacing={0} cellPadding={0} width="100%">
                            <tbody><tr>
                                <td style={{ paddingLeft: 15, color: 'white', fontWeight: 'bold', fontSize: 24 }}>
                                    Principle{principleList.length > 1 ? ` ${currentPrcIndex + 1} of ${principleList.length}` : ''}
                                </td>
                                <td align="right" valign="top">
                                    <img src={CloseBtnSrc} alt="Close"
                                        onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }}
                                        onMouseOver={() => setCloseBtnSrc(x_close_btn_o)} onMouseOut={() => setCloseBtnSrc(x_close_btn_n)}
                                        style={{ cursor: 'pointer', padding: 5, width: 40, height: 40, marginRight: 30 }} />
                                </td>
                            </tr></tbody>
                        </table>
                        <div id="divFramePCS">
                            {isPrcLoading ? <Loading /> : (
                                <iframe ref={prcIframeRef} title="Prc" srcDoc={prcContent}
                                    style={{ width: '100%', border: 'none', overflow: 'hidden', display: 'block' }}
                                    onLoad={() => {
                                        const iframe = prcIframeRef.current;
                                        if (!iframe?.contentDocument?.body) return;
                                        setTimeout(() => {
                                            const h = iframe.contentDocument!.body.scrollHeight + 20;
                                            iframe.style.height = `${Math.max(h, 80)}px`;
                                        }, 50);
                                    }}
                                />
                            )}
                        </div>
                        <table id="tblNavPCS" className="principle" cellSpacing={0} cellPadding={0} width="100%" style={{ height: 50, fontSize: 15 }}>
                            <tbody><tr style={{ backgroundColor: '#F2F2F2' }}>
                                <td align="left" style={{ width: '33%', paddingLeft: 15 }}>
                                    {currentPrcIndex > 0 && (
                                        <input type="button" value="PREVIOUS"
                                            style={{ fontWeight: 'bold', color: 'white', fontSize: '12pt', borderStyle: 'none', backgroundColor: 'maroon', cursor: 'pointer', padding: '5px 10px' }}
                                            onClick={() => {
                                                const prev = currentPrcIndex - 1;
                                                setCurrentPrcIndex(prev);
                                                const fp = new URLSearchParams(window.location.search).get('folder') || '';
                                                fetchPrincipleContent(principleList[prev], fp, params.get('country') || 'Australia').then(d => setPrcContent(d.principleHtml || ''));
                                            }} />
                                    )}
                                </td>
                                <td style={{ width: '34%' }} />
                                <td align="right" style={{ width: '33%', paddingRight: 15 }}>
                                    {currentPrcIndex < principleList.length - 1 ? (
                                        <img src={NextStepBtnSrc} alt="Next"
                                            onMouseOver={() => setNextStepBtnSrc(next_step_btn_o)} onMouseOut={() => setNextStepBtnSrc(next_step_btn_n)}
                                            style={{ cursor: 'pointer', width: 100, height: 40, marginRight: 30, marginTop: 5, marginBottom: 5 }}
                                            onClick={() => {
                                                const next = currentPrcIndex + 1;
                                                setCurrentPrcIndex(next);
                                                const fp = new URLSearchParams(window.location.search).get('folder') || '';
                                                fetchPrincipleContent(principleList[next], fp, params.get('country') || 'Australia').then(d => setPrcContent(d.principleHtml || ''));
                                            }} />
                                    ) : (
                                        <img src={GotItBtnSrc} alt="Got It"
                                            onMouseOver={() => setGotItBtnSrc(got_it_btn_o)} onMouseOut={() => setGotItBtnSrc(got_it_btn_n)}
                                            style={{ cursor: 'pointer', width: 100, height: 40, marginRight: 30, marginTop: 5, marginBottom: 5 }}
                                            onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }} />
                                    )}
                                </td>
                            </tr></tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ── Solution modal ── */}
            {showSolnModal && (
                <>
                    <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => { setShowSolnModal(false); setCurrentSolnNo(0); }} />
                    <div id="divPCS" style={{ display: 'block', position: 'fixed', bottom: 0, width: '100%', zIndex: 10000 }}>
                        <table id="tblTitlePCS" className="solution" cellSpacing={0} cellPadding={0} width="100%">
                            <tbody><tr>
                                <td id="tdPCSTitle1">Step by step solution</td>
                                <td align="right" valign="top">
                                    <img src={CloseBtnSrc} alt="Close"
                                        onClick={() => { setShowSolnModal(false); setCurrentSolnNo(0); }}
                                        onMouseOver={() => setCloseBtnSrc(x_close_btn_o)} onMouseOut={() => setCloseBtnSrc(x_close_btn_n)}
                                        style={{ cursor: 'pointer', padding: 5 }} />
                                </td>
                            </tr></tbody>
                        </table>
                        <div id="divFramePCS">
                            {isSolnLoading ? <Loading /> : (
                                <iframe ref={solnIframeRef} title="Solution" srcDoc={solnContent}
                                    style={{ width: '100%', border: 'none', minHeight: 50 }}
                                    onLoad={() => {
                                        const iframe = solnIframeRef.current;
                                        if (!iframe?.contentDocument?.body) return;
                                        try {
                                            const iframeDoc = iframe.contentDocument;
                                            const stepsInput = iframeDoc.getElementById('NoOfSteps') as HTMLInputElement;
                                            const total = parseInt(stepsInput?.value || '0', 10);
                                            setNoOfSteps(total);
                                            const isAutoExpanded = iframeDoc.getElementById('IsAutoExpanded');
                                            iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]').forEach((step, i) => {
                                                step.style.display = (i === 0 || isAutoExpanded) ? 'table-row' : 'none';
                                            });
                                            if (total === 0) iframe.style.height = '50px';
                                            setTimeout(() => {
                                                if (!iframe.contentDocument?.body) return;
                                                const h = iframe.contentDocument.body.scrollHeight + 5;
                                                iframe.style.height = `${Math.min(h, window.innerHeight * 0.4)}px`;
                                                iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);
                                            }, 50);
                                            iframeDoc.querySelectorAll<HTMLImageElement>('img[id^="KsdImg"]').forEach(img => {
                                                img.onclick = () => {
                                                    const idx = img.id.substring(6);
                                                    const ksdDiv = iframeDoc.getElementById(`KsdDiv${idx}`);
                                                    const content = ksdDiv?.getAttribute('data-ksd-content');
                                                    const parentTd = img.closest('td');
                                                    if (content && parentTd && img.getAttribute('alt') === '1') {
                                                        parentTd.innerHTML = content;
                                                        img.setAttribute('alt', '2');
                                                    }
                                                };
                                            });
                                        } catch (e) { console.error(e); }
                                    }}
                                />
                            )}
                        </div>
                        <table id="tblNavPCS" className="solution" cellSpacing={0} cellPadding={0} width="100%" style={{ backgroundColor: '#F2F2F2' }}>
                            <tbody><tr>
                                <td align="left" style={{ width: '33%', paddingLeft: 15 }} />
                                <td style={{ width: '34%' }} />
                                <td align="right" style={{ width: '33%', paddingRight: 15 }}>
                                    {!isSolnLoading && noOfSteps > 0 && currentSolnNo < noOfSteps - 1 && (
                                        <img src={NextStepBtnSrc} alt="Next Step" style={{ cursor: 'pointer' }}
                                            onMouseOver={() => setNextStepBtnSrc(next_step_btn_o)} onMouseOut={() => setNextStepBtnSrc(next_step_btn_n)}
                                            onClick={() => {
                                                const next = currentSolnNo + 1;
                                                const iframe = solnIframeRef.current;
                                                if (iframe?.contentDocument?.body) {
                                                    const steps = iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');
                                                    if (next < steps.length) {
                                                        steps[next].style.display = 'table-row';
                                                        setTimeout(() => iframe.contentWindow?.scrollTo(0, iframe.contentDocument!.body.scrollHeight), 0);
                                                    }
                                                    setCurrentSolnNo(next);
                                                    if (solutionSeen !== 'Y') setSolutionSeen(next + 1);
                                                }
                                            }}
                                        />
                                    )}
                                    {!isSolnLoading && (noOfSteps === 0 || currentSolnNo === noOfSteps - 1) && (
                                        <img src={GotItBtnSrc} alt="Got It" style={{ cursor: 'pointer' }}
                                            onMouseOver={() => setGotItBtnSrc(got_it_btn_o)} onMouseOut={() => setGotItBtnSrc(got_it_btn_n)}
                                            onClick={() => { setShowSolnModal(false); setCurrentSolnNo(0); setSolutionSeen('Y'); }} />
                                    )}
                                </td>
                            </tr></tbody>
                        </table>
                    </div>
                </>
            )}
        </>
    );
};

export default forwardRef(LQns);